import { useMemo } from 'react';

import {
  ExecutionStatusEnum,
  TotalModeEnum,
  getFragmentData,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { isDefined } from '@e-socna/utils';

import { SINGLETON_GROUP_LABEL } from '../../../components/ServiceUsages/GroupBanner';
import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../../../components/DocumentEditor/resolvers/fragments/quick-access.fragment';
import { getLiveServiceUsagesGroups } from '../../../components/DocumentEditor/resolvers/utils/mission-service-usages-groups.util';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useBuildPaths } from '../../../hooks/useBuildPaths.hook';
import { useMissionSpaceWording } from '../../../hooks/useMissionSpaceWording.hook';
import { PATH_MISSION_EXTERNAL_ACTORS_EDIT } from '../../../app/constants/paths.constant';
import { NewTabButton } from '../../../components/NewTabButton';
import type { ComplianceIssues } from '../../../components/ComplianceDialog';

import {
  missingBillingAddressIssue,
  quotationActorsBlockingIssues,
} from '../utils/quotation-actors-blocking-issues.util';

const EXTERNAL_ACTORS_PATHS = {
  externalActorsEdit: PATH_MISSION_EXTERNAL_ACTORS_EDIT,
};

export const useMissionQuotationComplianceIssues = (): ComplianceIssues => {
  const { mission } = useMissionReadyContext();
  const missionWording = useMissionSpaceWording();
  const pathsParams = useMemo(
    () => ({ folderId: mission.folderId, missionId: mission._id }),
    [mission.folderId, mission._id],
  );
  // The quotation contact is a representative of the receiver among the external actors.
  const {
    paths: { externalActorsEdit },
  } = useBuildPaths(EXTERNAL_ACTORS_PATHS, pathsParams);

  return useMemo(() => {
    const quickAccess = getFragmentData(
      DocumentEditorResolvers_QuickAccess_Mission_Fragment,
      mission,
    );

    const { quotationReceiverActor, quotationEmitterActor } =
      quickAccess.consolidation;
    const snapshot = quickAccess.quotationInfo?.snapshot;

    const effectiveServiceUsagesGroups = snapshot
      ? snapshot.serviceUsagesGroups
      : getLiveServiceUsagesGroups(quickAccess);
    const hasNoServiceUsage = effectiveServiceUsagesGroups.every(
      (group) => !group.serviceUsages.length,
    );

    const manualTotalGroupLabels = effectiveServiceUsagesGroups
      .filter((group) => group.totalMode === TotalModeEnum.Manual)
      .map((group) => group.label ?? SINGLETON_GROUP_LABEL);

    const effectiveTotalCalculation = snapshot
      ? snapshot.totalCalculation
      : quickAccess.servicesInfo.totalCalculation;
    // A negative global total is reported by the back as a Ko total
    // calculation ("Le total du devis ne peut pas être négatif."), never as a
    // negative result.
    const totalCalculationErrors =
      effectiveTotalCalculation?.status === ExecutionStatusEnum.Ko
        ? effectiveTotalCalculation.details.map(({ fr }) => fr)
        : EMPTY_ARRAY;

    const legalRepresentatives =
      quotationEmitterActor.__typename === 'ActorOrganization'
        ? quotationEmitterActor.consolidation.legalRepresentatives
        : null;

    return {
      blocking: [
        ...quotationActorsBlockingIssues(quickAccess.consolidation),
        // The emitter and place-and-date blocks of the document need it.
        missingBillingAddressIssue(quotationEmitterActor, {
          label: "L'émetteur du devis",
          ofLabel: "de l'émetteur du devis",
        }),
        hasNoServiceUsage
          ? { message: 'Le devis doit contenir au moins une prestation.' }
          : null,
        ...totalCalculationErrors.map((message) => ({ message })),
      ].filter(isDefined),
      warnings: [
        quotationReceiverActor.__typename === 'ActorOrganization' &&
        !quotationReceiverActor.consolidation.quotationContact
          ? {
              message:
                "Le destinataire du devis (organisation) n'a pas d'interlocuteur défini.",
              action: (
                <NewTabButton
                  href={externalActorsEdit}
                  label={`Modifier les acteurs externes ${missionWording.ofName}`}
                />
              ),
            }
          : null,
        !legalRepresentatives?.length
          ? {
              message:
                "L'émetteur du devis devrait avoir au moins un représentant légal parmi ses représentants.",
            }
          : null,
        isDefined(legalRepresentatives) &&
        legalRepresentatives.length &&
        legalRepresentatives.some((rep) => !rep.person.signatureImageUri)
          ? {
              message:
                "Certains représentants légaux de l'émetteur du devis n'ont pas défini leur signature.",
            }
          : null,
        manualTotalGroupLabels.length
          ? {
              message: `Le total des groupes suivants est saisi manuellement : ${manualTotalGroupLabels.join(', ')}. Les montants et coûts des prestations correspondantes ne seront pas affichés dans le devis (seules les quantités peuvent l'être).`,
            }
          : null,
      ].filter(isDefined),
    };
  }, [externalActorsEdit, mission, missionWording.ofName]);
};
